import * as React from 'react'
import { FormField } from '@/components/templates/entity-form'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { apiFieldMessages } from '@/lib/contacts/errors'
import type {
  CustomField,
  CustomFieldAccess,
  CustomFieldAppliesTo,
  CustomFieldSetupForm,
  CustomFieldType,
} from '@/lib/contacts/types'

const customFieldTypes: CustomFieldType[] = [
  'text',
  'multiline text',
  'number',
  'date',
  'checkbox',
  'single-select',
  'multi-select',
  'URL',
  'file',
]

function initialCustomFieldSetupForm(field?: CustomField): CustomFieldSetupForm {
  const emptyForm: CustomFieldSetupForm = {
    label: '',
    fieldType: 'text',
    appliesTo: 'both',
    fieldAccess: 'staff-only',
    askOnJoinForm: false,
    required: false,
    choices: '',
  }

  if (!field) return emptyForm

  return {
    label: field.label,
    fieldType: field.field_type,
    appliesTo: field.applies_to,
    fieldAccess: field.field_access,
    askOnJoinForm: field.ask_on_join_form,
    required: field.required,
    choices: field.choices.join(', '),
  }
}

function customFieldDialogTitle(editing: boolean, field?: CustomField) {
  return editing ? `Edit field - ${field?.label}` : 'New field'
}

function shouldShowChoices(form: CustomFieldSetupForm) {
  return form.fieldType === 'single-select' || form.fieldType === 'multi-select'
}

export function CustomFieldDialog({
  mode,
  field,
  pending,
  error,
  onClose,
  onSubmit,
  onConvertType,
  onManageChoices,
}: {
  mode: 'new' | 'edit'
  field?: CustomField
  pending: boolean
  error: Error | null
  onClose: () => void
  onSubmit: (form: CustomFieldSetupForm) => void
  onConvertType?: () => void
  onManageChoices?: () => void
}) {
  const editing = mode === 'edit'
  const [form, setForm] = React.useState<CustomFieldSetupForm>(() =>
    initialCustomFieldSetupForm(field),
  )
  const labelError = apiFieldMessages(error, 'label')[0]
  const choicesError = apiFieldMessages(error, 'choices')[0]

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault()
            onSubmit(form)
          }}
        >
          <DialogHeader>
            <DialogTitle>{customFieldDialogTitle(editing, field)}</DialogTitle>
          </DialogHeader>
          <div className="divide-y divide-border" data-testid="custom-field-editor-sections">
            <div className="pb-3">
              <FormField label="Label" htmlFor="custom-field-label" error={labelError}>
                <Input
                  id="custom-field-label"
                  value={form.label}
                  placeholder="e.g. License number"
                  disabled={editing}
                  onChange={(event) => setForm({ ...form, label: event.target.value })}
                />
              </FormField>
            </div>
            <div className="py-3">
              <CustomFieldTypeField
                editing={editing}
                form={form}
                onChange={setForm}
                onConvertType={onConvertType}
              />
            </div>
            <div className="py-3">
              <CustomFieldAppliesToField form={form} onChange={setForm} />
            </div>
            <div className="py-3">
              <CustomFieldAccessField form={form} onChange={setForm} />
            </div>
            <div className="py-3">
              <CustomFieldJoinFormFields form={form} onChange={setForm} />
            </div>
            {shouldShowChoices(form) && (
              <div className="pt-3">
                {!editing ? (
                  <CustomFieldChoicesField form={form} error={choicesError} onChange={setForm} />
                ) : (
                  <div className="flex items-center gap-2 text-body-sm">
                    <span className="text-muted-foreground">{form.choices || 'No choices'}</span>
                    <Button type="button" variant="outline" size="sm" onClick={onManageChoices}>
                      Manage choices…
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={pending || form.label.trim().length === 0}>
              {editing ? 'Save changes' : 'Create field'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function CustomFieldTypeField({
  editing,
  form,
  onChange,
  onConvertType,
}: {
  editing: boolean
  form: CustomFieldSetupForm
  onChange: (form: CustomFieldSetupForm) => void
  onConvertType?: () => void
}) {
  return (
    <FormField label="Type" htmlFor="custom-field-type">
      {editing ? (
        <div id="custom-field-type" className="flex items-center gap-2 text-body-sm">
          <span className="text-foreground">{form.fieldType}</span>
          <Button type="button" variant="outline" size="sm" onClick={onConvertType}>
            Convert type…
          </Button>
        </div>
      ) : (
        <Select
          value={form.fieldType}
          onValueChange={(fieldType) =>
            onChange({ ...form, fieldType: fieldType as CustomFieldType })
          }
        >
          <SelectTrigger id="custom-field-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {customFieldTypes.map((fieldType) => (
              <SelectItem key={fieldType} value={fieldType}>
                {fieldType}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </FormField>
  )
}

function CustomFieldAppliesToField({
  form,
  onChange,
}: {
  form: CustomFieldSetupForm
  onChange: (form: CustomFieldSetupForm) => void
}) {
  return (
    <FormField label="Applies to" htmlFor="custom-field-applies-to">
      <Select
        value={form.appliesTo}
        onValueChange={(appliesTo) =>
          onChange({ ...form, appliesTo: appliesTo as CustomFieldAppliesTo })
        }
      >
        <SelectTrigger id="custom-field-applies-to">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="People">People</SelectItem>
          <SelectItem value="Organizations">Organizations</SelectItem>
          <SelectItem value="both">People &amp; Orgs</SelectItem>
        </SelectContent>
      </Select>
    </FormField>
  )
}

function CustomFieldAccessField({
  form,
  onChange,
}: {
  form: CustomFieldSetupForm
  onChange: (form: CustomFieldSetupForm) => void
}) {
  return (
    <FormField label="Field access" htmlFor="custom-field-access">
      <Select
        value={form.fieldAccess}
        onValueChange={(fieldAccess) =>
          onChange({ ...form, fieldAccess: fieldAccess as CustomFieldAccess })
        }
      >
        <SelectTrigger id="custom-field-access">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="staff-only">Staff only</SelectItem>
          <SelectItem value="view-only">View only</SelectItem>
          <SelectItem value="members may edit">Members may edit</SelectItem>
        </SelectContent>
      </Select>
    </FormField>
  )
}

function CustomFieldJoinFormFields({
  form,
  onChange,
}: {
  form: CustomFieldSetupForm
  onChange: (form: CustomFieldSetupForm) => void
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <CustomFieldCheckbox
        id="custom-field-ask-on-join-form"
        label="Ask on join form"
        checked={form.askOnJoinForm}
        onCheckedChange={(checked) => onChange({ ...form, askOnJoinForm: checked })}
      />
      <CustomFieldCheckbox
        id="custom-field-required"
        label="Required"
        checked={form.required}
        onCheckedChange={(checked) => onChange({ ...form, required: checked })}
      />
    </div>
  )
}

function CustomFieldCheckbox({
  id,
  label,
  checked,
  onCheckedChange,
}: {
  id: string
  label: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}) {
  return (
    <div className="flex items-center gap-2">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(nextChecked) => onCheckedChange(nextChecked === true)}
      />
      <Label htmlFor={id} className="text-foreground">
        {label}
      </Label>
    </div>
  )
}

function CustomFieldChoicesField({
  form,
  error,
  onChange,
}: {
  form: CustomFieldSetupForm
  error?: string
  onChange: (form: CustomFieldSetupForm) => void
}) {
  return (
    <FormField label="Choices" htmlFor="custom-field-choices" error={error}>
      <Input
        id="custom-field-choices"
        value={form.choices}
        placeholder="Bay Area, Pacific Northwest"
        onChange={(event) => onChange({ ...form, choices: event.target.value })}
      />
    </FormField>
  )
}
